import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ClipboardList, Clock, History as HistoryIcon, UserRound } from "lucide-react";

const PRIORITY_COLORS = { LOW: "#22c55e", NORMAL: "#2e7cba", HIGH: "#f59e0b", URGENT: "#ef4444" };
const STATUS_COLORS = {
  PENDING: "#94a3b8",
  IN_PROGRESS: "#2e7cba",
  COMPLETED: "#22c55e",
  CANCELLED: "#ef4444",
};
const PRIORITY_LABEL_KEY = { LOW: "priorityLow", NORMAL: "priorityNormal", HIGH: "priorityHigh", URGENT: "priorityUrgent" };
const STATUS_LABEL_KEY = {
  PENDING: "statusPending",
  IN_PROGRESS: "statusInProgress",
  COMPLETED: "statusCompleted",
  CANCELLED: "statusCancelled",
};

// Detail view for one task — role-conditional actions matching the backend's
// exact ownership split (plan §4.4): the OWNER may only move status
// (Start/Complete); the ASSIGNER may edit details or cancel. History (plan
// §4.2b/§7 of the web plan) is fetched lazily, only while this modal is open.
// Small colored pill — used for Status/Priority so they read as clear badges
// instead of plain colored text, matching the badge convention used
// elsewhere in this codebase (e.g. status pills on Employee Actions/
// Advancement cards).
const Badge = ({ color, children }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      fontSize: 12,
      fontWeight: 700,
      padding: "4px 12px",
      borderRadius: 20,
      color,
      border: `1.5px solid ${color}`,
      background: `${color}18`,
    }}
  >
    {children}
  </span>
);

const TaskDetailModal = ({
  show,
  task,
  assigneeName,
  currentInfinityCode,
  history,
  historyLoading,
  styles,
  screenSize,
  t,
  language,
  onClose,
  onStart,
  onComplete,
  onCancelTask,
  onEdit,
  onFetchHistory,
}) => {
  const [actionPending, setActionPending] = useState(false);

  useEffect(() => {
    if (show && task) onFetchHistory(task.id);
    setActionPending(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, task?.id]);

  if (!show || !task) return null;

  // Disable immediately on click, before the request even resolves — the
  // same double-submit guard used elsewhere in this codebase (e.g. the
  // Advancement approval screens) so a fast double-click can never fire two
  // overlapping status-change requests. The backend's idempotent same-status
  // handling (plan §4.4.3) is the second line of defense, not the only one.
  const guardedAction = (fn) => async (t) => {
    if (actionPending) return;
    setActionPending(true);
    try {
      await fn(t);
    } finally {
      setActionPending(false);
    }
  };
  const handleStart = guardedAction(onStart);
  const handleComplete = guardedAction(onComplete);
  const handleCancelTask = guardedAction(onCancelTask);

  const isOwner = String(task.infinityCode) === String(currentInfinityCode);
  const isAssigner = task.assignedBy != null && String(task.assignedBy) === String(currentInfinityCode);
  const isAssignedTask = task.assignedBy != null;
  const canStart = isOwner && isAssignedTask && task.status === "PENDING";
  const canComplete = isOwner && isAssignedTask && ["PENDING", "IN_PROGRESS"].includes(task.status);
  const canCancel = isAssigner && !["COMPLETED", "CANCELLED"].includes(task.status);
  const canEdit = isAssigner;

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString(language === "ar" ? "ar-EG" : "en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(language === "ar" ? "ar-EG" : "en-GB");
    } catch {
      return iso;
    }
  };

  const fieldLabel = (field) =>
    ({
      title: t.taskTitle,
      description: t.taskDescription,
      status: t.filterStatus,
      priority: t.priority,
      dueDate: t.dueDate,
    }[field] || field);

  // Portal to document.body — same containing-block fix as TaskFormModal
  // (the Tasks panel's `backdropFilter` traps `position: fixed` descendants
  // to its own bounds otherwise).
  return createPortal(
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modalContainer, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitleSection}>
            <div style={{ ...styles.modalIcon, background: STATUS_COLORS[task.status] }}>
              <ClipboardList size={screenSize.isMobile ? 22 : 26} />
            </div>
            <div>
              <h2 style={styles.modalTitle}>{task.title}</h2>
              <p style={styles.modalSubtitle}>{t[STATUS_LABEL_KEY[task.status]]}</p>
            </div>
          </div>
          <button style={styles.closeButton} onClick={onClose} type="button">
            <X size={screenSize.isMobile ? 20 : 24} />
          </button>
        </div>

        <div style={styles.formContainer}>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <Badge color={STATUS_COLORS[task.status]}>{t[STATUS_LABEL_KEY[task.status]]}</Badge>
            <Badge color={PRIORITY_COLORS[task.priority]}>{t[PRIORITY_LABEL_KEY[task.priority]]}</Badge>
          </div>

          {task.description && (
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{t.taskDescription}</label>
              <p style={{ margin: 0, color: styles.formInput.color }}>{task.description}</p>
            </div>
          )}

          {/* Full details grid — always shown, per the "show every detail"
              requirement: who it's for, who assigned it, and every date. */}
          <div style={styles.formGrid}>
            {isAssignedTask && (
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{t.assignTo}</label>
                <span style={{ fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <UserRound size={14} /> {assigneeName || task.infinityCode}
                </span>
              </div>
            )}
            {isAssignedTask && (
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{t.assignedBy}</label>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <UserRound size={14} /> {task.assignedByName || task.assignedBy}
                </span>
              </div>
            )}
          </div>

          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>{t.dueDate}</label>
              <span>{formatDate(task.dueDate)}</span>
            </div>
            {task.completedAt && (
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>{t.statusCompleted}</label>
                <span>{formatDate(task.completedAt)}</span>
              </div>
            )}
          </div>

          {isAssignedTask && (
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <HistoryIcon size={14} /> {t.taskHistory}
                </span>
              </label>
              {historyLoading ? (
                <Clock size={16} />
              ) : history.length === 0 ? (
                <p style={{ margin: 0, opacity: 0.7, fontSize: 13 }}>{t.noTaskHistory}</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {history.map((entry) => (
                    <div key={entry.id} style={{ fontSize: 13, opacity: 0.85 }}>
                      <strong>{fieldLabel(entry.field)}</strong>: {entry.oldValue ?? "—"} → {entry.newValue ?? "—"}
                      <div style={{ fontSize: 11, opacity: 0.6 }}>{formatDateTime(entry.changedAt)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={styles.formActions}>
            {canStart && (
              <button
                type="button"
                style={styles.actionButton("default")}
                onClick={() => handleStart(task)}
                disabled={actionPending}
              >
                {t.startTask}
              </button>
            )}
            {canComplete && (
              <button
                type="button"
                style={styles.actionButton("success")}
                onClick={() => handleComplete(task)}
                disabled={actionPending}
              >
                {t.completeTask}
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                style={styles.actionButton("default")}
                onClick={() => onEdit(task)}
                disabled={actionPending}
              >
                {t.editTask}
              </button>
            )}
            {canCancel && (
              <button
                type="button"
                style={styles.actionButton("danger")}
                onClick={() => {
                  if (window.confirm(t.confirmCancelTask)) handleCancelTask(task);
                }}
                disabled={actionPending}
              >
                {t.cancelTask}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TaskDetailModal;
